// src/pages/PropertiesPage.js
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useProperties } from "../hooks/useProperties";
import PropertyCard from "../components/PropertyCard";
import Spinner from "../components/Spinner";
import { LOCATIONS, PROPERTY_TYPES, BUDGET_RANGES } from "../utils/constants";

export default function PropertiesPage() {
  const [params, setParams] = useSearchParams();

  const [type, setType]         = useState(params.get("type") || "");
  const [location, setLocation] = useState(params.get("location") || "");
  const [budget, setBudget]     = useState(0); // index into BUDGET_RANGES
  const [search, setSearch]     = useState(params.get("q") || "");
  const [showFilters, setShowFilters] = useState(false);

  const selectedBudget = BUDGET_RANGES[budget];

  const { properties, loading, hasMore, loadMore, refresh } = useProperties({
    type: type || undefined,
    location: location || undefined,
    minPrice: selectedBudget.min || undefined,
    maxPrice: selectedBudget.max === Infinity ? undefined : selectedBudget.max,
  });

  // Client-side search filter
  const filtered = search
    ? properties.filter(p =>
        p.title?.toLowerCase().includes(search.toLowerCase()) ||
        p.location?.toLowerCase().includes(search.toLowerCase()) ||
        p.description?.toLowerCase().includes(search.toLowerCase())
      )
    : properties;

  const clearFilters = () => { setType(""); setLocation(""); setBudget(0); setSearch(""); };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "20px 16px" }}>
      <h1 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 22, color: "#1a2e1a", marginBottom: 4 }}>Properties in Satna</h1>
      <p style={{ color: "#6b7c6b", fontSize: 14, marginBottom: 18 }}>Buy, rent or invest — find the right property today</p>

      {/* Search + filter toggle */}
      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
          <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 15 }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search properties..."
            style={{ width: "100%", padding: "10px 14px 10px 34px", border: "1.5px solid #e2ece2", borderRadius: 8, fontFamily: "inherit", fontSize: 14, color: "#1a2e1a", background: "white" }} />
        </div>
        <button onClick={() => setShowFilters(s => !s)} style={{
          padding: "10px 16px", borderRadius: 8, border: `1.5px solid #1a6b3c`,
          background: showFilters ? "#1a6b3c" : "white", color: showFilters ? "white" : "#1a6b3c",
          fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6
        }}>⚙️ Filters {(type || location || budget > 0) ? "●" : ""}</button>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div style={{ background: "white", border: "1px solid #e2ece2", borderRadius: 12, padding: 16, marginBottom: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10 }}>
          <FilterSelect label="Property Type" value={type} onChange={setType} options={["", ...PROPERTY_TYPES]} labels={["All Types", ...PROPERTY_TYPES]} />
          <FilterSelect label="Location" value={location} onChange={setLocation} options={["", ...LOCATIONS]} labels={["All Areas", ...LOCATIONS]} />
          <FilterSelect label="Budget" value={budget} onChange={v => setBudget(Number(v))} options={BUDGET_RANGES.map((_, i) => i)} labels={BUDGET_RANGES.map(b => b.label)} />
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <button onClick={clearFilters} style={{ width: "100%", padding: "10px", borderRadius: 8, border: "1px solid #e2ece2", background: "white", color: "#6b7c6b", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Quick type pills */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {["", ...PROPERTY_TYPES].map((t, i) => (
          <button key={i} onClick={() => setType(t)} style={{
            padding: "5px 13px", borderRadius: 20, fontSize: 12, fontWeight: 600,
            border: "1px solid #e2ece2",
            background: type === t ? "#1a6b3c" : "white",
            color: type === t ? "white" : "#1a2e1a",
            cursor: "pointer", fontFamily: "inherit"
          }}>{t || "All"}</button>
        ))}
      </div>

      {/* Results count */}
      <p style={{ fontSize: 13, color: "#6b7c6b", marginBottom: 16 }}>
        <b style={{ color: "#1a2e1a" }}>{filtered.length}</b> properties found
        {(type || location) && <> in <b style={{ color: "#1a6b3c" }}>{[type, location].filter(Boolean).join(", ")}</b></>}
      </p>

      {/* Grid */}
      {loading && properties.length === 0 ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#6b7c6b" }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>🏘️</div>
          <p style={{ fontWeight: 700, fontSize: 16 }}>No properties found</p>
          <p style={{ fontSize: 13 }}>Try adjusting your filters</p>
          <button onClick={clearFilters} style={{ marginTop: 16, padding: "9px 20px", borderRadius: 8, background: "#1a6b3c", color: "white", border: "none", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Clear Filters</button>
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(275px,1fr))", gap: 16 }}>
            {filtered.map(p => <PropertyCard key={p.id} property={p} />)}
          </div>
          {hasMore && (
            <div style={{ textAlign: "center", marginTop: 28 }}>
              <button onClick={loadMore} disabled={loading} style={{
                padding: "12px 32px", borderRadius: 10, background: "white", border: "2px solid #1a6b3c",
                color: "#1a6b3c", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit"
              }}>{loading ? "Loading..." : "Load More Properties"}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, labels }) {
  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7c6b", display: "block", marginBottom: 5 }}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #e2ece2", borderRadius: 8, fontFamily: "inherit", fontSize: 14, background: "white", color: "#1a2e1a" }}>
        {options.map((o, i) => <option key={i} value={o}>{labels[i]}</option>)}
      </select>
    </div>
  );
}
